import type { MeResponse } from "@seagames/contracts";
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { NoAccess } from "../components/auth/no-access";
import { SessionChecking } from "../components/auth/session-screens";
import { AppShell } from "../components/shell/app-shell";
import { ApiError } from "../lib/api/client";
import { type OvrAccess, ovrAccessFor } from "../lib/auth/access";
import { SessionUnreachableError, checkSession } from "../lib/auth/session-check";
import { hasStoredSession } from "../lib/auth/token-store";

/**
 * The signed-in shell: the sidebar, the top bar and the page
 * (`components/shell/app-shell.tsx`). The guard runs in `beforeLoad`, so no
 * page renders — not for a frame — before the server has said who is asking. Meanwhile the
 * bare band and "Checking your session…" show from the first frame
 * (`pendingComponent`, no delay), and if the server does not answer, the
 * root error boundary shows the self-retrying "Can't reach the server"
 * screen after ONE attempt (`checkSession`) — never a blank page.
 *
 * Route context for every page below: `{ session, access, readOnly }`.
 * `access` is decided from the ACTIVE role only.
 */
export const Route = createFileRoute("/_app")({
	beforeLoad: async ({ context, location }) => {
		if (!hasStoredSession()) {
			throw redirect({ to: "/login", search: { redirect: location.href } });
		}
		let session: MeResponse;
		try {
			session = await checkSession(context.queryClient);
		} catch (err) {
			// No answer is not a verdict: retry screen, session intact.
			if (err instanceof ApiError && err.transient) throw new SessionUnreachableError(err);
			throw redirect({ to: "/login", search: { redirect: location.href } });
		}
		if (session.must_change_password) {
			throw redirect({ to: "/change-password", search: { redirect: location.href } });
		}
		const access: OvrAccess = ovrAccessFor(session.active_role);
		return { session, access, readOnly: access !== "write" };
	},
	pendingComponent: SessionChecking,
	pendingMs: 0,
	pendingMinMs: 0,
	component: AppLayout,
});

function AppLayout() {
	const { session, access, readOnly } = Route.useRouteContext();
	// No <Outlet/> without access, so nothing below mounts or fetches.
	if (access === "none") return <NoAccess session={session} />;
	return (
		<AppShell session={session} readOnly={readOnly}>
			<Outlet />
		</AppShell>
	);
}
